import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  ScrollView,
  Text,
  View,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Switch,
  Platform,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AppLoading from 'expo-app-loading';
import { useFonts } from 'expo-font';

// API per i permessi
import * as Location from 'expo-location';
import { useCameraPermissions } from 'expo-camera';
import * as Contacts from 'expo-contacts';
import * as MediaLibrary from 'expo-media-library';
import * as NotificationsLib from 'expo-notifications';

// Firebase
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  onValue,
  set,
  update,
} from 'firebase/database';

// Configurazione Firebase (con le credenziali fornite)
const firebaseConfig = {
  apiKey: "AIzaSyDnl8ZvUpEyjClgwYSb5APcU24zY-X0H8U",
  authDomain: "ngl-cila.firebaseapp.com",
  databaseURL: "https://ngl-cila-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "ngl-cila",
  storageBucket: "ngl-cila.firebasestorage.app",
  messagingSenderId: "1049554300483",
  appId: "1:1049554300483:web:a023e58e8c5a8969cc6ef6",
  measurementId: "G-54W482ES88"
};

if (!getApps().length) {
  initializeApp(firebaseConfig);
}
const auth = getAuth();
const db = getDatabase();
const googleProvider = new GoogleAuthProvider();

// Traduzione degli errori Firebase in italiano
const translateFirebaseError = (error) => {
  switch (error.code) {
    case 'auth/invalid-email':
      return 'Indirizzo email non valido.';
    case 'auth/user-disabled':
      return 'Utente disabilitato.';
    case 'auth/user-not-found':
      return 'Utente non trovato.';
    case 'auth/wrong-password':
      return 'Password errata.';
    case 'auth/email-already-in-use':
      return 'Questa email è già in uso.';
    case 'auth/weak-password':
      return 'La password è troppo debole.';
    case 'auth/operation-not-allowed':
      return 'Operazione non consentita.';
    case 'auth/network-request-failed':
      return 'Errore di rete. Controlla la tua connessione.';
    default:
      return error.message;
  }
};

// Helper per aggiornare i dati dell'utente in Firebase
export const updateUserData = async (userId, data) => {
  if (userId) {
    const userRef = ref(db, 'users/' + userId);
    try {
      await update(userRef, data);
    } catch (error) {
      console.error("Errore nell'aggiornamento dei dati utente:", error);
    }
  }
};

// Funzione per convertire una chiave VAPID (in base64) in Uint8Array (necessaria sul web)
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = typeof window !== "undefined" && window.atob
    ? window.atob(base64)
    : Buffer.from(base64, 'base64').toString('binary');
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Il componente AccountScreen include funzionalità di login/registrazione, toggles per notifiche, modalità scura,
// localizzazione, fotocamera, contatti, galleria e visualizzazione versione dell'app.
const AccountScreen = () => {
  // Caricamento del font personalizzato
  const [fontsLoaded] = useFonts({
    MyCustomFont: require('../../assets/font.ttf'),
  });

  // Stati di autenticazione e utente
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);

  // Stati per il form di Login/Registrazione
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [authError, setAuthError] = useState('');

  // Stati per le impostazioni (dopo il login)
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [language, setLanguage] = useState('Italiano');
  const [locationPermission, setLocationPermission] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(false);
  const [contactsPermission, setContactsPermission] = useState(false);
  const [mediaPermission, setMediaPermission] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const appVersion = "v1.2.0";

  // Hook per i permessi della fotocamera (Expo)
  const [cameraPermissionStatus, requestCameraPermission] = useCameraPermissions();

  // Osserva lo stato dell'autenticazione
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userRef = ref(db, 'users/' + currentUser.uid);
        onValue(userRef, (snapshot) => {
          const data = snapshot.val();
          setUsername(data && data.username ? data.username : '');
        });
      } else {
        setUsername('');
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Se il font non è ancora caricato
  if (!fontsLoaded) {
    return <AppLoading />;
  }

  // Funzione per il login con email/password
  const handleLogin = async () => {
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setAuthError(translateFirebaseError(error));
    }
  };

  // Funzione per la registrazione con email/password
  const handleRegister = async () => {
    setAuthError('');
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;
      // Salva username ed email nel database
      await set(ref(db, 'users/' + currentUser.uid), {
        username: usernameInput,
        email: email,
      });
    } catch (error) {
      setAuthError(translateFirebaseError(error));
    }
  };

  // Funzione per il logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Errore durante il logout:", error);
    }
  };

  // Funzione per il login con Google
  const handleGoogleSignIn = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setAuthError(translateFirebaseError(error));
    }
  };

  // Funzione helper per richiedere permessi, indipendentemente dalla piattaforma
  const togglePermission = async (value, setter, mobileRequest, dbField, webFallback) => {
    let granted = false;
    if (value) {
      if (Platform.OS === "web") {
        granted = webFallback ? await webFallback() : true;
      } else {
        const { status } = await mobileRequest();
        granted = (status === "granted");
      }
    }
    setter(granted);
    await updateUserData(user.uid, { [dbField]: granted });
  };

  // Gestione del permesso di localizzazione
  const handleLocationToggle = async (value) => {
    let granted = false;
    if (value) {
      if (Platform.OS === "web") {
        granted = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(true),
            () => resolve(false)
          );
        });
      } else {
        const { status } = await Location.requestForegroundPermissionsAsync();
        granted = (status === 'granted');
      }
    }
    setLocationPermission(granted);
    await updateUserData(user.uid, { locationPermission: granted });
  };

  // Gestione del permesso della fotocamera
  const handleCameraToggle = async (value) => {
    await togglePermission(
      value,
      setCameraPermission,
      requestCameraPermission,
      "cameraPermission",
      async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach((track) => track.stop());
          return true;
        } catch (error) {
          return false;
        }
      }
    );
  };

  // Gestione del permesso per i contatti
  const handleContactsToggle = async (value) => {
    await togglePermission(
      value,
      setContactsPermission,
      Contacts.requestPermissionsAsync,
      "contactsPermission",
      async () => true // Su web si assume il permesso
    );
  };

  // Gestione del permesso per la galleria/media library
  const handleMediaToggle = async (value) => {
    await togglePermission(
      value,
      setMediaPermission,
      MediaLibrary.requestPermissionsAsync,
      "mediaPermission",
      async () => true // Su web si assume il permesso
    );
  };

  // Gestione delle notifiche (diversa a seconda della piattaforma)
  const handleNotificationsSwitch = async (value) => {
    if (Platform.OS === 'web') {
      if (value) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          alert('Permesso per le notifiche non concesso');
          setNotificationsEnabled(false);
          await updateUserData(user.uid, { token_fcm: null });
          return;
        }
        // Registra il Service Worker
        let registration;
        try {
          registration = await navigator.serviceWorker.register('/sw.js');
          console.log("Service Worker registrato:", registration);
        } catch (error) {
          console.error("Errore nella registrazione del Service Worker:", error);
          return;
        }
        const swRegistration = await navigator.serviceWorker.ready;
        // Verifica eventuali sottoscrizioni esistenti e disiscrivile
        const existingSubscription = await swRegistration.pushManager.getSubscription();
        if (existingSubscription) {
          await existingSubscription.unsubscribe();
          console.log("Sottoscrizione esistente disiscritta");
        }
        // Sottoscrizione al PushManager: sostituisci 'YOUR_VAPID_KEY' col valore corretto
        const subscription = await swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            'BC-5SGUmwdnUV99Utqcc7WzNNE7KDawLEjnwr1IBMW3Fq6iMyX6Zu7mfCu2xdKsb8GbCxtCj4QuZBFgi42bnFEc'
          ),
        });
        console.log("Web push subscription:", subscription);
        // Salva la sottoscrizione in Firebase (la chiave token_fcm conterrà il JSON della sottoscrizione)
        await updateUserData(user.uid, { token_fcm: JSON.stringify(subscription) });
        setNotificationsEnabled(true);
      } else {
        await updateUserData(user.uid, { token_fcm: null });
        setNotificationsEnabled(false);
      }
    } else {
      // Su mobile, usiamo expo-notifications
      if (value) {
        let finalStatus;
        const { status: existingStatus } = await NotificationsLib.getPermissionsAsync();
        finalStatus = existingStatus;
        if (existingStatus !== 'granted') {
          const { status } = await NotificationsLib.requestPermissionsAsync();
          finalStatus = status;
        }
        if (finalStatus !== 'granted') {
          alert('Permesso per le notifiche non concesso');
          setNotificationsEnabled(false);
          await updateUserData(user.uid, { token_fcm: null });
          return;
        }
        const tokenResponse = await NotificationsLib.getExpoPushTokenAsync();
        const token = tokenResponse.data;
        await updateUserData(user.uid, { token_fcm: token });
        setNotificationsEnabled(true);
      } else {
        await updateUserData(user.uid, { token_fcm: null });
        setNotificationsEnabled(false);
      }
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#BB86FC" />
      </SafeAreaView>
    );
  }

  // Se l'utente non è autenticato mostra form Login/Registrazione
  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={[styles.headerTitle, { fontFamily: 'MyCustomFont' }]}>Account</Text>
        </View>
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={[styles.sectionTitle, { fontFamily: 'MyCustomFont' }]}>
            {isRegisterMode ? 'Registrazione' : 'Login'}
          </Text>
          {isRegisterMode && (
            <View style={styles.inputContainer}>
              <TextInput
                placeholder="Username"
                placeholderTextColor="#888"
                style={[styles.input, { fontFamily: 'MyCustomFont' }]}
                value={usernameInput}
                onChangeText={setUsernameInput}
              />
            </View>
          )}
          <View style={styles.inputContainer}>
            <TextInput
              placeholder="Email"
              placeholderTextColor="#888"
              style={[styles.input, { fontFamily: 'MyCustomFont' }]}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>
          <View style={styles.inputContainer}>
            <TextInput
              placeholder="Password"
              placeholderTextColor="#888"
              style={[styles.input, { fontFamily: 'MyCustomFont' }]}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
            />
          </View>
          {authError ? (
            <Text style={[styles.errorText, { fontFamily: 'MyCustomFont' }]}>{authError}</Text>
          ) : null}
          <TouchableOpacity
            style={styles.authButton}
            onPress={isRegisterMode ? handleRegister : handleLogin}
          >
            <Text style={[styles.buttonText, { fontFamily: 'MyCustomFont' }]}>
              {isRegisterMode ? 'Registrati' : 'Accedi'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.googleButton}
            onPress={handleGoogleSignIn}
          >
            <Ionicons name="logo-google" size={24} color="#fff" />
            <Text style={[styles.buttonText, { fontFamily: 'MyCustomFont', marginLeft: 10 }]}>
              Accedi con Google
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setIsRegisterMode(!isRegisterMode);
              setAuthError('');
            }}
          >
            <Text style={[styles.switchText, { fontFamily: 'MyCustomFont' }]}>
              {isRegisterMode ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
            </Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Se l'utente è autenticato mostra le info e le impostazioni dell'account
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { fontFamily: 'MyCustomFont' }]}>
          Benvenuto, {username ? username : user.email}
        </Text>
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.infoContainer}>
          <Ionicons name="person-circle-outline" size={80} color="#BB86FC" />
          <Text style={[styles.infoText, { fontFamily: 'MyCustomFont' }]}>
            {username ? username : user.email}
          </Text>
        </View>
        <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
          <Text style={[styles.buttonText, { fontFamily: 'MyCustomFont' }]}>Logout</Text>
        </TouchableOpacity>
        <Text style={[styles.sectionTitle, { fontFamily: 'MyCustomFont' }]}>
          Impostazioni di Base
        </Text>
        {/* Impostazioni Notifiche */}
        <View style={styles.item}>
          <Ionicons name="notifications-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Notifiche</Text>
          <TouchableOpacity
            style={styles.authButton}
            onPress={async () => {
              if (!notificationsEnabled) {
                await handleNotificationsSwitch(true);
              } else {
                await handleNotificationsSwitch(false);
              }
            }}
          >
            <Text style={[styles.buttonText, { fontFamily: 'MyCustomFont' }]}>
              {notificationsEnabled ? "Disabilita Notifiche" : "Abilita Notifiche"}
            </Text>
          </TouchableOpacity>
        </View>
        {/* Impostazioni Modalità Scura */}
        <View style={styles.item}>
          <Ionicons name="moon-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Modalità Scura</Text>
          <Switch
            value={darkModeEnabled}
            onValueChange={async (value) => {
              setDarkModeEnabled(value);
              await updateUserData(user.uid, { darkModeEnabled: value });
            }}
            thumbColor={darkModeEnabled ? "#BB86FC" : "#888"}
          />
        </View>
        {/* Impostazioni Lingua */}
        <TouchableOpacity style={styles.item}>
          <Ionicons name="language-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Lingua</Text>
          <Text style={[styles.itemValue, { fontFamily: 'MyCustomFont' }]}>{language}</Text>
          <Ionicons name="chevron-forward-outline" size={20} color="#ccc" />
        </TouchableOpacity>
        <Text style={[styles.sectionTitle, { fontFamily: 'MyCustomFont' }]}>
          Altre Impostazioni e Permessi
        </Text>
        {/* Permessi Localizzazione */}
        <View style={styles.item}>
          <Ionicons name="location-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Permessi Localizzazione</Text>
          <Switch
            value={locationPermission}
            onValueChange={handleLocationToggle}
            thumbColor={locationPermission ? "#BB86FC" : "#888"}
          />
        </View>
        {/* Permessi Fotocamera */}
        <View style={styles.item}>
          <Ionicons name="camera-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Permessi Fotocamera</Text>
          <Switch
            value={cameraPermission}
            onValueChange={handleCameraToggle}
            thumbColor={cameraPermission ? "#BB86FC" : "#888"}
          />
        </View>
        {/* Permessi Contatti */}
        <View style={styles.item}>
          <Ionicons name="people-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Permessi Contatti</Text>
          <Switch
            value={contactsPermission}
            onValueChange={handleContactsToggle}
            thumbColor={contactsPermission ? "#BB86FC" : "#888"}
          />
        </View>
        {/* Permessi Galleria */}
        <View style={styles.item}>
          <Ionicons name="images-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Permessi Galleria</Text>
          <Switch
            value={mediaPermission}
            onValueChange={handleMediaToggle}
            thumbColor={mediaPermission ? "#BB86FC" : "#888"}
          />
        </View>
        {/* Privacy & Sicurezza */}
        <TouchableOpacity style={styles.item}>
          <Ionicons name="shield-checkmark-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Privacy & Sicurezza</Text>
          <Ionicons name="chevron-forward-outline" size={20} color="#ccc" />
        </TouchableOpacity>
        {/* Informazioni di Versione */}
        <View style={styles.item}>
          <Ionicons name="information-circle-outline" size={24} color="#BB86FC" />
          <Text style={[styles.itemText, { fontFamily: 'MyCustomFont' }]}>Versione dell'App</Text>
          <Text style={[styles.itemValue, { fontFamily: 'MyCustomFont' }]}>{appVersion}</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#121212',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#121212',
  },
  header: {
    paddingVertical: 20,
    backgroundColor: '#1f1f1f',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  content: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 22,
    color: '#fff',
    marginBottom: 20,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 15,
  },
  input: {
    backgroundColor: '#1e1e1e',
    color: '#fff',
    padding: 15,
    borderRadius: 10,
    fontSize: 16,
  },
  authButton: {
    backgroundColor: '#BB86FC',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginVertical: 15,
  },
  googleButton: {
    flexDirection: 'row',
    backgroundColor: '#DB4437',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginVertical: 15,
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
  },
  switchText: {
    color: '#BB86FC',
    fontSize: 16,
    textAlign: 'center',
  },
  errorText: {
    color: 'red',
    textAlign: 'center',
    marginBottom: 15,
  },
  infoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 30,
  },
  infoText: {
    fontSize: 20,
    color: '#fff',
    marginTop: 10,
  },
  logoutButton: {
    backgroundColor: '#BB86FC',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 30,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1e1e1e',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  itemText: {
    flex: 1,
    marginLeft: 10,
    fontSize: 16,
    color: '#fff',
  },
  itemValue: {
    fontSize: 16,
    color: '#ccc',
    marginRight: 5,
  },
});

export default AccountScreen;
